// The cohort chosen on the homepage, once checkout knows the real list.
//
// The choice arrives as `?grupa=<id>` and used to be re-asked: the form kept
// showing the whole carousel under "Alege grupa *", with the chosen card
// ticked somewhere inside it. Now the choice is confirmed and the list waits
// behind a link (29.09.2026).
import { isFull, scheduleLabel, formatStartDate, FORMAT_LABEL } from "./groupFormat";

// Why a cohort chosen a few seconds ago can no longer be joined. Both end the
// same way — the list opens — but the reason is said out loud, because today
// the selection is dropped in silence and the person is left with an
// unanswered "Alege grupa *".
export const DROP_NOTES = {
  full: "Grupa aleasă s-a ocupat între timp. Mai jos sunt grupele cu locuri libere.",
  // One note for every other disappearance: the public list drops a cohort
  // whose enrolments have closed (o oră înainte de start) exactly as it drops
  // one taken off the site, so the browser cannot tell those two apart.
  gone: "Grupa aleasă nu mai primește înscrieri. Alege altă grupă dintre cele de mai jos.",
};

/**
 * What to do with `?grupa=<id>` once `/groups/active` has answered.
 *
 *   none     → nobody chose anything; the list is the question, as before
 *   loading  → the id is known, the list is not yet
 *   chosen   → confirm it, and keep the list one click away
 *   dropped  → open the list, with `note` saying why
 */
export function preselectOutcome(groups, wantedId, failed = false) {
  if (!wantedId) return { state: "none", group: null, note: "" };
  // The list failed to load: there is nothing to confirm the id against and
  // nothing more to wait for, so the section falls back to the list, which
  // shows the reason. The id itself is kept — the server validates it anyway.
  if (failed) return { state: "none", group: null, note: "" };
  if (!Array.isArray(groups)) return { state: "loading", group: null, note: "" };
  const match = groups.find((g) => g?.id === wantedId);
  if (!match) return { state: "dropped", group: null, note: DROP_NOTES.gone };
  if (isFull(match)) return { state: "dropped", group: null, note: DROP_NOTES.full };
  return { state: "chosen", group: match, note: "" };
}

/** The three lines of the confirmation strip. Empty parts are dropped. */
export function chosenLines(group) {
  if (!group) return { name: "", start: "", meta: "" };
  const parts = [scheduleLabel(group), FORMAT_LABEL[group.format] || group.format];
  const count = (group.sessions || []).length;
  if (count) parts.push(`${count} ${count === 1 ? "sesiune" : "sesiuni"}`);
  return {
    name: group.name || "",
    start: group.start_date ? `Start: ${formatStartDate(group.start_date)}` : "",
    meta: parts.filter(Boolean).join(" · "),
  };
}

/**
 * Whether the confirmation strip stands in for the list.
 *
 * The rule, in one place so the heading, the asterisk and the body of the
 * section cannot disagree: an id still being resolved shows the strip (the
 * alternative flashes "Alege grupa *" and then takes the asterisk back), a
 * confirmed cohort shows it until somebody asks for the list, and choosing
 * from that list closes it again — the decision was made, and it belongs
 * where the last one was.
 */
export function showsStrip(outcome, listOpen) {
  if (!outcome) return false;
  if (outcome.state === "loading") return true;
  return outcome.state === "chosen" && !listOpen;
}

// The heading above the section. With the choice already made there is nothing
// to choose, so the required-field asterisk goes too.
export function sectionLabel(collapsed) {
  return collapsed ? "Grupa ta" : "Alege grupa";
}
